import { useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "i18n";
import { Popup } from "component-library";
import { parseQuery, parseToUrlQuery } from "shared/utils/http";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetAboutMe } from "pages/Profile/hooks/useGetAboutMe";
import { useUpdateAboutMe } from "pages/Profile/hooks/useUpdateAboutMe";
import {
  useGetPronounsPronunciation,
  IPronounsPronunciation,
} from "pages/Profile/hooks/useGetPronounsPronunciation";
import { useUpdatePronounsPronunciation } from "pages/Profile/hooks/useUpdatePronounsPronunciation";
import { UserClaimTypes, UserManager } from "app/core";
import HeaderActions from "pages/Profile/components/ProfileHeader/components/HeaderActions";
import type { ToastState } from "pages/Profile/components/ProfileHeader/components/HeaderActions";
import ProfileLogos from "pages/Profile/components/ProfileHeader/components/ProfileLogos";
import PronounsPronunciationSection from "pages/Profile/components/ProfileHeader/components/PronounsPronunciationSection";
import { parseProfileIdentityValidationErrors } from "pages/Profile/utils/parseProfileIdentityValidationErrors";
import {
  MAX_HIGHLIGHTS_CHAR_LENGTH,
  MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH,
  MAX_HIGHLIGHTS_WARNING_CHAR_LENGTH,
  HIGHLIGHTS_DISPLAY_LINE_THRESHOLD,
} from "./constants";
import {
  expandableTextAreaStorageHtmlToDraftString,
  HTML_TO_TEXT_REPLACEMENTS,
  sanitizeTextForRequestPayload,
  sanitizeExpandableTextAreaForStorage,
} from "shared/utils/dom/sanitizeExpandableTextAreaHtml";
import { useGetTranslationContent } from "pages/Profile/hooks/useGetTranslationContent";
import { sendEmail } from "pages/Profile/helpers/sendEmail";
import { runBrowserPrint } from "pages/Profile/helpers/runBrowserPrint";
import ShareViaEmail from "pages/Profile/components/common/ShareViaEmail";
import { usePrintDataIsReady } from "pages/Profile/hooks/usePrintDataIsReady";
import {
  heapTrackingForAboutMePage,
  heapTrackingProfileEdit,
} from "app/helpers/heapTracking";
import ExpandableTextArea from "../common/ExpandableTextArea";
import { checkIsYp } from "shared-auth";
import { useGetProfileBasicInfoSource } from "pages/Profile/hooks/useGetProfileBasicInfoSource";

import "./ProfileHeaderEditSection.scss";

const PROFILE_QUERY_ACTION = {
  email: "email",
  print: "print",
} as const;

const processedProfileQuerySearches = new Set<string>();
let pendingPrintFromProfileQuery = false;

interface Props {
  isEdit?: boolean;
  toggleEdit?: () => void;
}

const ProfileHeader = ({ isEdit, toggleEdit }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const printDataIsReady = usePrintDataIsReady();
  const { isPublicView } = usePublicView();
  const viewPin = UserManager.getClaim(UserClaimTypes.viewPin);
  const fullName = UserManager.getClaimWithDefault(
    UserClaimTypes.profileFullName
  );

  const { rawPublicProfile } = useGetProfileBasicInfoSource();
  const isYoungPerformer = checkIsYp(rawPublicProfile?.artistType);

  const { aboutMe } = useGetAboutMe();
  const updateAboutMe = useUpdateAboutMe();
  const { data: pAndPData = { pronouns: "", pronunciation: "" } } =
    useGetPronounsPronunciation();
  const updatePronounsPronunciation = useUpdatePronounsPronunciation();

  const [isEmailPopupVisible, setIsEmailPopupVisible] = useState(false);
  const [toastState, setToastState] = useState<ToastState>({
    isOpen: false,
    message: "",
  });
  const [editingPandP, setEditingPandP] = useState(false);
  const [editingHighlights, setEditingHighlights] = useState(false);

  const [draftPandP, setDraftPandP] = useState<IPronounsPronunciation>({
    pronouns: "",
    pronunciation: "",
  });
  const [draftHighlights, setDraftHighlights] = useState("");
  const [pandPErrors, setPandPErrors] = useState<{
    pronouns?: string;
    pronunciation?: string;
  }>({});
  const [highlightsError, setHighlightsError] = useState<string | null>(null);
  const [pandPStash, setPandPStash] = useState(false);
  const [highlightsStash, setHighlightsStash] = useState(false);

  const headerEditExitSnapRef = useRef({
    editingPandP: false,
    editingHighlights: false,
    draftPandP: { pronouns: "", pronunciation: "" } as IPronounsPronunciation,
    pAndPData: { pronouns: "", pronunciation: "" } as IPronounsPronunciation,
    draftHighlights: "",
    aboutAppearance: "" as string | undefined,
  });

  headerEditExitSnapRef.current = {
    editingPandP,
    editingHighlights,
    draftPandP,
    pAndPData,
    draftHighlights,
    aboutAppearance: aboutMe?.currentAppearance,
  };

  useEffect(() => {
    if (isEdit) return;

    const editExitSnapRef = headerEditExitSnapRef.current;
    const apiP = editExitSnapRef.pAndPData.pronouns ?? "";
    const apiPr = editExitSnapRef.pAndPData.pronunciation ?? "";
    const pandpUnsaved =
      editExitSnapRef.editingPandP &&
      (editExitSnapRef.draftPandP.pronouns !== apiP ||
        editExitSnapRef.draftPandP.pronunciation !== apiPr);

    const savedHighlights = sanitizeExpandableTextAreaForStorage(
      editExitSnapRef.aboutAppearance ?? ""
    );
    const highlightsUnsaved =
      editExitSnapRef.editingHighlights &&
      sanitizeExpandableTextAreaForStorage(editExitSnapRef.draftHighlights) !==
        savedHighlights;

    if (pandpUnsaved) setPandPStash(true);

    if (highlightsUnsaved) setHighlightsStash(true);

    setEditingPandP(false);
    setEditingHighlights(false);
    setPandPErrors({});
    setHighlightsError(null);
  }, [isEdit]);

  useLayoutEffect(() => {
    const search = location.search;
    const { action, ...queryRest } = parseQuery(search);

    if (
      !action ||
      (action !== PROFILE_QUERY_ACTION.email &&
        action !== PROFILE_QUERY_ACTION.print)
    ) {
      processedProfileQuerySearches.clear();

      return;
    }

    if (processedProfileQuerySearches.has(search)) return;

    processedProfileQuerySearches.add(search);
    history.replace(location.pathname + parseToUrlQuery(queryRest));

    if (action === PROFILE_QUERY_ACTION.email) {
      setIsEmailPopupVisible(true);

      return;
    }

    pendingPrintFromProfileQuery = true;
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    if (!pendingPrintFromProfileQuery || !printDataIsReady) return;

    pendingPrintFromProfileQuery = false;
    runBrowserPrint();
  }, [printDataIsReady]);

  const content = useGetTranslationContent();

  const highlightsText = editingHighlights
    ? draftHighlights
    : (aboutMe?.currentAppearance ?? "");
  const pronounsDisplay = editingPandP
    ? draftPandP.pronouns
    : pAndPData.pronouns;
  const pronunciationDisplay = editingPandP
    ? draftPandP.pronunciation
    : pAndPData.pronunciation;
  const hasPandP = Boolean(
    editingPandP
      ? draftPandP.pronouns || draftPandP.pronunciation
      : pAndPData.pronouns || pAndPData.pronunciation
  );
  const hasHighlights = Boolean(
    editingHighlights ? draftHighlights : aboutMe?.currentAppearance
  );

  const openPandPEdit = () => {
    if (!pandPStash) {
      setDraftPandP({
        pronouns: pAndPData.pronouns ?? "",
        pronunciation: pAndPData.pronunciation ?? "",
      });
    }

    setPandPErrors({});
    setEditingPandP(true);
  };

  const closePandPEdit = () => {
    setEditingPandP(false);
    setPandPStash(false);
    setDraftPandP({ pronouns: "", pronunciation: "" });
    setPandPErrors({});
  };

  const openHighlightsEdit = () => {
    if (!highlightsStash) {
      setDraftHighlights(
        expandableTextAreaStorageHtmlToDraftString(
          aboutMe?.currentAppearance ?? ""
        )
      );
    }

    setHighlightsError(null);
    setEditingHighlights(true);
    heapTrackingProfileEdit(true);
  };

  const closeHighlightsEdit = () => {
    setEditingHighlights(false);
    setHighlightsStash(false);
    setDraftHighlights("");
    setHighlightsError(null);
  };

  const handleSavePandP = () => {
    const maxLen = MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH;
    const msg = t("performer:profile.header.validation.charLimit", {
      maxLength: maxLen,
    });
    const errors: { pronouns?: string; pronunciation?: string } = {};

    if (draftPandP.pronouns.length > maxLen) errors.pronouns = msg;

    if (draftPandP.pronunciation.length > maxLen) errors.pronunciation = msg;

    if (Object.keys(errors).length > 0) {
      setPandPErrors(errors);

      return;
    }

    setPandPErrors({});

    updatePronounsPronunciation.mutate(draftPandP, {
      onSuccess: () => closePandPEdit(),
      onError: (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          const next = parseProfileIdentityValidationErrors(
            error.response.data
          );

          if (Object.keys(next).length > 0) {
            setPandPErrors(next);
          }
        }
      },
    });
  };

  const handleSaveHighlights = () => {
    const toSave = sanitizeTextForRequestPayload(
      sanitizeExpandableTextAreaForStorage(draftHighlights),
      HTML_TO_TEXT_REPLACEMENTS
    );

    if (toSave.length > MAX_HIGHLIGHTS_CHAR_LENGTH) {
      setHighlightsError(
        t("performer:profile.header.validation.charLimit", {
          maxLenght: MAX_HIGHLIGHTS_CHAR_LENGTH,
        })
      );

      return;
    }

    setHighlightsError(null);

    updateAboutMe.mutate(
      { currentAppearance: toSave },
      {
        onSuccess: () => {
          heapTrackingForAboutMePage(true);
          closeHighlightsEdit();
        },
      }
    );
  };

  const pAndPInvalid =
    draftPandP.pronouns.length > MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH ||
    draftPandP.pronunciation.length > MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH;

  const highlightsInvalid =
    sanitizeExpandableTextAreaForStorage(draftHighlights).length >
    MAX_HIGHLIGHTS_CHAR_LENGTH;
  const pAndPSaveDisabled =
    pAndPInvalid ||
    (draftPandP.pronouns === pAndPData.pronouns &&
      draftPandP.pronunciation === pAndPData.pronunciation) ||
    updatePronounsPronunciation.isLoading;

  const highlightsSaveDisabled =
    highlightsInvalid ||
    sanitizeExpandableTextAreaForStorage(draftHighlights) ===
      sanitizeExpandableTextAreaForStorage(aboutMe?.currentAppearance ?? "") ||
    updateAboutMe.isLoading;

  const handleCopyLinkClick = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/${viewPin}`);
    setToastState((prev) => ({
      ...prev,
      message: t("performer:profile.header.notificationToast.message"),
      isOpen: true,
    }));
  };

  const handleHideToast = () => {
    setToastState((prev) => ({ ...prev, message: "", isOpen: false }));
  };

  const handleDraftPandPChange = (draft: IPronounsPronunciation) => {
    setDraftPandP(draft);
    setPandPErrors({});
  };

  const openEmailPopup = () => setIsEmailPopupVisible(true);

  const closeEmailPopup = () => setIsEmailPopupVisible(false);

  const stretchHighlightsToVideo =
    (hasHighlights && !editingHighlights) || editingHighlights;

  const highlightsSectionProps = {
    content: highlightsText,
    hasContent: hasHighlights,
    editExpandableTextArea: editingHighlights,
    draftContent: draftHighlights,
    editExpandableTextAreaError: highlightsError,
    isSaveButtonDisabled: highlightsSaveDisabled,
    isEdit,
    isPublicView,
    alignWithVideoColumn: stretchHighlightsToVideo,
    onOpenEdit: openHighlightsEdit,
    onCloseEdit: closeHighlightsEdit,
    onSave: handleSaveHighlights,
    onDraftChange: setDraftHighlights,
    emptyFieldClassName: "italic text-color-neutral-two dark:text-white/70",
    lineClamp: HIGHLIGHTS_DISPLAY_LINE_THRESHOLD,
    isEditButtonVisible: true,
    maxLength: MAX_HIGHLIGHTS_CHAR_LENGTH,
    warningLength: MAX_HIGHLIGHTS_WARNING_CHAR_LENGTH,
    placeholder: t("performer:profile.header.highlightsPlaceholder"),
    emptyFieldText: t("performer:profile.header.addHighlight"),
  };

  const highlightsVideoWidthClass = "lg:max-w-[calc(75%-12px)]";

  const pAndPSectionProps = {
    pronounsDisplay,
    pronunciationDisplay,
    hasPandP,
    editingPandP,
    draftPandP,
    pandPErrors,
    pAndPSaveDisabled,
    isSavingPandP: updatePronounsPronunciation.isLoading,
    isEdit,
    isPublicView,
    onOpenEdit: openPandPEdit,
    onCloseEdit: closePandPEdit,
    onSave: handleSavePandP,
    onDraftChange: handleDraftPandPChange,
  };

  return (
    <>
      <header className="mb-6 flex flex-col gap-6">
        <HeaderActions
          isEdit={isEdit}
          toggleEdit={toggleEdit}
          toastState={toastState}
          onHideToast={handleHideToast}
          onCopyLink={handleCopyLinkClick}
          onOpenEmailPopup={openEmailPopup}
        />
        <div className="flex flex-col gap-2">
          <div
            className={cx("grid items-start md:gap-x-4", {
              "grid-cols-[minmax(0,1fr)_157px] md:grid-cols-[minmax(0,1fr)_auto]":
                isYoungPerformer,
              "grid-cols-[minmax(0,1fr)_auto] gap-x-3": !isYoungPerformer,
            })}
          >
            <div className="contents md:flex md:min-w-0 md:flex-col md:gap-2">
              <h1
                className={cx(
                  "col-start-1 row-start-1 md:col-auto md:row-auto",
                  "flex flex-wrap items-start gap-4 md:items-baseline",
                  "uppercase",
                  (!fullName || fullName.length <= 20) &&
                    "text-[2.5rem] md:text-[3.75rem]",
                  fullName &&
                    fullName.length > 20 &&
                    fullName.length <= 30 &&
                    "text-[2rem] md:text-[2.5rem]",
                  fullName &&
                    fullName.length > 30 &&
                    "text-[1.5rem] md:text-[2rem]"
                )}
              >
                <span className="min-w-0 break-words">{fullName}</span>
                <PronounsPronunciationSection
                  layout="inline"
                  {...pAndPSectionProps}
                />
              </h1>

              <PronounsPronunciationSection
                layout="block"
                {...pAndPSectionProps}
              />

              {!stretchHighlightsToVideo && (
                <ExpandableTextArea {...highlightsSectionProps} />
              )}
            </div>

            <div className="col-start-2 row-start-1 shrink-0 md:col-auto md:row-auto">
              <ProfileLogos />
            </div>
          </div>
          {stretchHighlightsToVideo && (
            <div
              className={cx(
                "w-full min-w-0 md:max-w-full",
                highlightsVideoWidthClass
              )}
            >
              <ExpandableTextArea {...highlightsSectionProps} />
            </div>
          )}
        </div>
      </header>
      {isEmailPopupVisible && (
        <Popup
          width={{ lg: 6, md: 8 }}
          priority="medium"
          close={closeEmailPopup}
        >
          <ShareViaEmail
            content={content.emailForm}
            sendEmail={sendEmail}
            closeForm={closeEmailPopup}
          />
        </Popup>
      )}
    </>
  );
};

export default ProfileHeader;
